import type { Option } from '@/entities/user-config';
import { DASHBOARD_PREFIX } from '@/features/dashboard/constants/const';
import {
  buildDashboardPayload,
  getEffectiveTabName,
  normalizeTabEntry
} from '@/features/dashboard/lib/transformers';
import type {
  DashboardTab,
  MarketApps,
  NestedDashboardStructure,
  TabEntry
} from '@/features/dashboard/types';
import { generateUniqueUUID } from '@/shared/lib/format/generateUniqueUUID';

import type { BuildMergedPayloadParams, DashboardUpdateInput } from './dashboardMutation.types';

export { getEffectiveTabName };

// ============================================================================
// Type guards
// ============================================================================

const isTabEntry = (value: MarketApps | TabEntry): value is TabEntry =>
  !Array.isArray(value) && 'id' in value && 'content' in value && Array.isArray(value.content);

export const isFullDashboardStructure = (
  input: DashboardUpdateInput,
  dashboardName: string
): input is NestedDashboardStructure => {
  if (Array.isArray(input)) return false;
  if (!(dashboardName in input)) return false;
  return Object.keys(input[dashboardName]).length > 0;
};

// ============================================================================
// Pure helpers
// ============================================================================

export const isRenameOperation = (oldName: string | undefined, newName: string): boolean =>
  Boolean(oldName && oldName !== newName);

export const homepageMatchesDashboard = (homepageValue: string, dashboardName: string): boolean =>
  homepageValue === DASHBOARD_PREFIX + dashboardName || homepageValue === dashboardName;

export const buildDashboardPath = (dashboardName: string, tabName?: string): string => {
  const effectiveTab = getEffectiveTabName(tabName);
  return `/dashboard/${encodeURIComponent(dashboardName)}/${encodeURIComponent(effectiveTab)}`;
};

export const createHomepageOption = (dashboardName: string): Option => ({
  label: dashboardName,
  value: DASHBOARD_PREFIX + dashboardName
});

export const buildOptimisticDeleteCache = (
  cache: Record<string, DashboardTab>,
  dashboardName: string
): Record<string, DashboardTab> => {
  const updated = { ...cache };
  delete updated[dashboardName];
  return updated;
};

export const buildOptimisticCreateCache = (
  cache: Record<string, DashboardTab>,
  dashboardName: string,
  tab: DashboardTab
): Record<string, DashboardTab> => ({ ...cache, [dashboardName]: tab });

export const buildOptimisticSaveCache = (
  cache: Record<string, DashboardTab>,
  payload: Record<string, DashboardTab>,
  dashboardName: string,
  initialDashboardName: string
): Record<string, DashboardTab> => {
  const result = { ...cache, ...payload };
  if (dashboardName !== initialDashboardName && initialDashboardName in result) {
    delete result[initialDashboardName];
  }
  return result;
};

export const getPostDeleteNavTarget = (
  cache: Record<string, DashboardTab>
): { dashboardName: string; tabName: string } | null => {
  const [firstDashboard] = Object.keys(cache);
  if (!firstDashboard) return null;
  const [firstTab] = Object.keys(cache[firstDashboard] ?? {});
  if (!firstTab) return null;
  return { dashboardName: firstDashboard, tabName: firstTab };
};

export const buildMergedPayload = ({
  data,
  dashboardName,
  tabName,
  oldDashboardName,
  cached
}: BuildMergedPayloadParams): DashboardTab => {
  if (isFullDashboardStructure(data, dashboardName)) {
    return data[dashboardName];
  }

  const newTabPayload = buildDashboardPayload(data, dashboardName, tabName);
  const sourceDashboardName = oldDashboardName || dashboardName;
  const existingTabs = cached?.[sourceDashboardName] ?? {};
  const effectiveTabName = getEffectiveTabName(tabName);

  const updatedTabs: DashboardTab = {};
  for (const [name, value] of Object.entries(existingTabs)) {
    updatedTabs[name] = normalizeTabEntry(name, value);
  }

  const newTabValue = newTabPayload[effectiveTabName];
  const existingTabValue = existingTabs[effectiveTabName] as MarketApps | TabEntry | undefined;
  const normalizedExisting = existingTabValue
    ? normalizeTabEntry(effectiveTabName, existingTabValue)
    : null;

  if (isTabEntry(newTabValue)) {
    updatedTabs[effectiveTabName] = {
      id: normalizedExisting?.id || newTabValue.id,
      content: newTabValue.content
    };
  } else {
    updatedTabs[effectiveTabName] = {
      id: normalizedExisting?.id || generateUniqueUUID(new Set()),
      content: Array.isArray(data) ? data : []
    };
  }

  return updatedTabs;
};
